// ===================================================================
// KAIXO - Admin "Inversores" — métricas que un VC quiere ver
// ===================================================================
// Solo accesible para admin_general. Muestra cada métrica con:
//   - Valor actual
//   - Target deseable
//   - Semáforo (excellent/good/warn/bad/info)
//   - Consejo concreto si está por debajo del target
//   - Explicación de por qué importa
//
// Filosofía: pantalla diseñada para enseñarse en una negociación con
// inversores. Muestra fortalezas y riesgos honestamente.
// ===================================================================

import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import './AdminInvestorSection.css';

const SECTION_LABELS = {
    crecimiento: { label: 'Crecimiento y escala', icon: '📈' },
    engagement: { label: 'Engagement', icon: '🔥' },
    retencion: { label: 'Retención', icon: '🔄' },
    monetizacion: { label: 'Monetización', icon: '💰' },
    viralidad: { label: 'Viralidad / Adquisición', icon: '🚀' },
    unitEconomics: { label: 'Unit economics', icon: '⚖️' },
    geografia: { label: 'Geografía e idiomas', icon: '🌍' }
};

const STATUS_META = {
    excellent: { color: '#16a34a', bg: '#dcfce7', label: 'Excelente' },
    good:      { color: '#22c55e', bg: '#e8f5e9', label: 'Bueno' },
    warn:      { color: '#f59e0b', bg: '#fff7e6', label: 'Atención' },
    bad:       { color: '#ef4444', bg: '#fff0f0', label: 'Crítico' },
    info:      { color: '#6b7280', bg: '#f3f4f6', label: 'Info' },
    unknown:   { color: '#9ca3af', bg: '#f3f4f6', label: 'Sin datos' }
};

export default function AdminInvestorSection() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        api.get('/admin/investor-metrics')
            .then(({ data }) => { if (!cancelled) setData(data); })
            .catch(err => {
                if (cancelled) return;
                setError(err.response?.data?.error || err.message);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [reloadKey]);

    const grouped = (data?.metrics || []).reduce((acc, m) => {
        if (!acc[m.section]) acc[m.section] = [];
        acc[m.section].push(m);
        return acc;
    }, {});

    return (
        <div className="ai-root">
            <div className="ai-head">
                <h2 className="ai-title">📊 Parámetros</h2>
                <button className="ai-refresh" onClick={() => setReloadKey(k => k + 1)} disabled={loading}>
                    {loading ? '⏳' : '↻'} Actualizar
                </button>
            </div>

            <div className="ai-warning">
                🔒 <strong>Solo admin_general.</strong> Esta pantalla muestra información sensible
                (revenue, churn, costes). Diseñada para revisión interna y para enseñar en directo
                a un posible inversor durante una negociación. No compartir capturas.
            </div>

            {loading && <div className="ai-loading">Calculando métricas en tiempo real…</div>}
            {error && <div className="ai-error">Error: {error}</div>}

            {data && (
                <>
                    {/* Score general */}
                    <div className="ai-score-card">
                        <div className="ai-score-num">{data.summary?.investorReadinessScore || 0}<span className="ai-score-max">/10</span></div>
                        <div className="ai-score-label">{data.summary?.investorReadinessLabel}</div>
                        <div className="ai-score-grid">
                            <div className="ai-score-block ai-score-strengths">
                                <h4>✓ Fortalezas</h4>
                                {data.summary?.strengths?.length > 0 ? (
                                    <ul>{data.summary.strengths.map(s => <li key={s}>{s}</li>)}</ul>
                                ) : <p className="ai-score-empty">Aún no hay métricas en verde.</p>}
                            </div>
                            <div className="ai-score-block ai-score-risks">
                                <h4>⚠ Riesgos críticos</h4>
                                {data.summary?.risks?.length > 0 ? (
                                    <ul>{data.summary.risks.map(r => <li key={r}>{r}</li>)}</ul>
                                ) : <p className="ai-score-empty">Sin riesgos críticos identificados.</p>}
                            </div>
                        </div>
                    </div>

                    {/* Métricas agrupadas por sección */}
                    {Object.entries(grouped).map(([section, metrics]) => {
                        const meta = SECTION_LABELS[section] || { label: section, icon: '📌' };
                        return (
                            <div key={section} className="ai-section">
                                <h3 className="ai-section-title">
                                    <span className="ai-section-icon">{meta.icon}</span>
                                    {meta.label}
                                </h3>
                                <div className="ai-metrics-grid">
                                    {metrics.map(m => <MetricCard key={m.key} m={m} />)}
                                </div>
                            </div>
                        );
                    })}

                    <div className="ai-foot">
                        Generado: {new Date(data.generatedAt).toLocaleString('es-ES')}
                    </div>
                </>
            )}
        </div>
    );
}

function MetricCard({ m }) {
    const status = STATUS_META[m.status] || STATUS_META.unknown;
    return (
        <div
            className={`ai-metric ai-metric-${m.status}`}
            style={{ borderLeftColor: status.color }}
        >
            <div className="ai-metric-head">
                <span className="ai-metric-label">{m.label}</span>
                <span
                    className="ai-metric-status"
                    style={{ background: status.bg, color: status.color }}
                >
                    {status.label}
                </span>
            </div>
            <div className="ai-metric-value">{m.formatted}</div>
            {m.target != null && m.status !== 'info' && (
                <div className="ai-metric-target">Objetivo: <strong>{typeof m.target === 'number' && m.target < 100 ? m.target + (m.key.includes('Pct') || m.key.includes('Ret') || m.key === 'churn' || m.key.includes('conversion') || m.key.includes('Margin') || m.key.includes('intl') ? '%' : '') : m.target}</strong></div>
            )}
            {m.trend && <div className="ai-metric-trend">{m.trend}</div>}
            {m.explanation && (
                <details className="ai-metric-explain">
                    <summary>¿Por qué importa?</summary>
                    <p>{m.explanation}</p>
                </details>
            )}
            {m.advice && (
                <div className="ai-metric-advice">
                    💡 <strong>Consejo:</strong> {m.advice}
                </div>
            )}
        </div>
    );
}
